import { Link, useParams, useSearchParams } from "react-router-dom"
import {
  Arrow,
  ButtonLight,
  EyebrowLabel,
  FinalCta,
  Icon,
  Img,
  Prose,
  Reveal,
  Section,
  SectionHeader,
  TextLink,
} from "../lib/shared"
import { useCollection } from "../lib/content"
import { initials } from "../lib/format"
import NotFound from "./NotFound"
import { Seo, breadcrumbSchema, caseStudySchema } from "../lib/seo"

/*
  Case study page. The order follows how people actually read one: who and
  what (hero, with the result up front), a look at the work (cover), the facts
  beside the story (sticky panel and body), proof (results, gallery, quote),
  then somewhere to go next.
*/
export default function WorkDetail() {
  const { slug } = useParams()
  const [params] = useSearchParams()
  const { items } = useCollection("caseStudies")
  const published = items.filter((w) => w.status === "Published")

  // Unpublished case studies are reachable only via an explicit ?preview=1
  // link from the studio, and that view is never indexed.
  const preview = params.get("preview") === "1"
  const found = items.find((w) => w.slug === slug)
  const work = found && (found.status === "Published" || preview) ? found : null
  if (!work) return <NotFound />

  const idx = published.findIndex((w) => w.slug === work.slug)
  const next = published[(idx + 1) % published.length] ?? work
  const results = work.results.filter((r) => r.n)
  const hasQuote = Boolean(work.quote.q)
  const hasNext = next.slug !== work.slug
  const meta = [work.industry, work.location, work.year].filter(Boolean)
  // Only ever an http(s) link: the API enforces it, this guards older records.
  const liveUrl = /^https?:\/\//i.test(work.liveUrl ?? "") ? work.liveUrl : undefined

  const facts = [
    { k: "Client", v: work.client },
    { k: "Industry", v: work.industry },
    { k: "Location", v: work.location },
    { k: "Year", v: work.year },
  ].filter((f) => f.v)

  return (
    <>
      <Seo
        title={work.seoTitle || `${work.client} case study`}
        description={work.seoDescription || work.summary}
        image={work.cover}
        type="article"
        noindex={work.status !== "Published"}
        canonicalPath={`/work/${work.slug}`}
        article={{
          modifiedTime: work.updated,
          section: work.industry,
          tags: work.services,
        }}
        jsonLd={[
          caseStudySchema(work),
          breadcrumbSchema([
            { name: "Work", path: "/work" },
            { name: work.client, path: `/work/${work.slug}` },
          ]),
        ]}
      />

      {/* Hero: who, what, and the result up front */}
      <section className="relative isolate overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_90%_at_100%_0%,rgba(23,76,138,0.55)_0%,transparent_65%)]" />
        <div className="mx-auto max-w-[1280px] px-6 pb-16 pt-36 lg:px-10 lg:pb-20 lg:pt-44">
          <Reveal>
            <Link
              to="/work"
              className="group inline-flex items-center gap-2 text-[13px] font-medium text-neutral-400 transition-colors hover:text-white"
            >
              <Arrow className="h-4 w-4 rotate-180 transition-transform duration-300 group-hover:-translate-x-1" />
              All work
            </Link>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end lg:gap-16">
            <div className="lg:col-span-8">
              {meta.length > 0 && (
                <Reveal delay={60}>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent-bright">
                    {meta.join(" · ")}
                  </p>
                </Reveal>
              )}
              <Reveal delay={120}>
                <h1 className="mt-5 text-[clamp(2.6rem,5.4vw,4.4rem)] font-black leading-[1.02] tracking-[-0.035em]">
                  {work.client}
                </h1>
              </Reveal>
              {work.summary && (
                <Reveal delay={180}>
                  <p className="mt-6 max-w-2xl text-[18px] leading-[1.6] text-neutral-300">
                    {work.summary}
                  </p>
                </Reveal>
              )}
              {liveUrl && (
                <Reveal delay={220}>
                  <div className="mt-9">
                    <ButtonLight href={liveUrl}>
                      {work.liveLabel || "View site"}
                    </ButtonLight>
                  </div>
                </Reveal>
              )}
            </div>

            {work.headlineResult && (
              <Reveal delay={240} className="lg:col-span-4">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
                    Headline result
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-[clamp(1.7rem,2.6vw,2.2rem)] font-black leading-[1.1] tracking-[-0.02em] text-white">
                    <Icon
                      name="rising"
                      className="mt-1.5 h-7 w-7 shrink-0 text-accent-bright"
                    />
                    {work.headlineResult}
                  </p>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Cover. Straddles the hero and the page below, so the image is the
          first thing the eye lands on after the title. */}
      <section className="bg-[linear-gradient(to_bottom,var(--color-ink)_50%,var(--color-paper)_50%)]">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <Reveal>
            <Img
              src={work.cover}
              priority
              natural
              monogram={initials(work.client)}
              alt={`${work.client} project`}
              width={1600}
              height={1000}
              className="w-full rounded-3xl"
            />
          </Reveal>
        </div>
      </section>

      {/* The story, with the facts alongside */}
      <Section tone="paper">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-neutral-200 bg-mist p-7 lg:sticky lg:top-28">
              <EyebrowLabel>At a glance</EyebrowLabel>
              <dl className="mt-6 space-y-5">
                {facts.map((f) => (
                  <div key={f.k}>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                      {f.k}
                    </dt>
                    <dd className="mt-1 text-[15px] font-semibold text-ink">
                      {f.v}
                    </dd>
                  </div>
                ))}
                {work.services.length > 0 && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                      What we did
                    </dt>
                    <dd>
                      <ul className="mt-2 space-y-2">
                        {work.services.map((s) => (
                          <li
                            key={s}
                            className="flex items-start gap-2.5 text-[15px] leading-snug text-neutral-700"
                          >
                            <Icon
                              name="check"
                              className="mt-0.5 h-4 w-4 shrink-0 text-accent"
                            />
                            <span className="first-letter:uppercase">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
              </dl>
              <div className="mt-7 border-t border-neutral-200 pt-6">
                <TextLink to="/contact">Start a similar project</TextLink>
              </div>
            </div>
          </aside>

          <article className="min-w-0 lg:col-span-8">
            <Reveal>
              <div className="max-w-[720px]">
                <Prose text={work.body || work.summary} />
              </div>
            </Reveal>
          </article>
        </div>
      </Section>

      {/* Results */}
      {results.length > 0 && (
        <Section tone="ink">
          <SectionHeader
            light
            eyebrow="The outcome"
            title="What changed"
            intro={`The numbers ${work.client} cares about, measured after launch.`}
          />
          <div
            className={`grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 ${
              results.length >= 3 ? "lg:grid-cols-3" : ""
            }`}
          >
            {results.map((r, i) => (
              <Reveal key={`${r.n}-${r.l}`} delay={i * 90}>
                <div className="border-l border-white/15 pl-6">
                  <div className="text-[clamp(2.8rem,4.6vw,4rem)] font-black leading-none tracking-[-0.03em] text-white">
                    {r.n}
                  </div>
                  <div className="mt-3 text-[15px] text-neutral-400">{r.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {/* Gallery */}
      {work.gallery.length > 0 && (
        <Section tone="paper" className={results.length ? "" : "pt-0!"}>
          {/*
            Every image at its own shape, uncropped: the first full width, the
            rest in two balanced columns, so mixed portrait and landscape
            shots stack without gaps or letterboxing.
          */}
          <Reveal>
            <Img
              src={work.gallery[0]!}
              natural
              alt={`${work.client} project image 1`}
              className="w-full rounded-2xl"
            />
          </Reveal>
          {work.gallery.length > 1 && (
            <div className="mt-6 gap-6 sm:columns-2">
              {work.gallery.slice(1).map((g, i) => (
                <Reveal
                  key={g}
                  delay={(i % 2) * 90}
                  className="mb-6 break-inside-avoid last:mb-0"
                >
                  <Img
                    src={g}
                    natural
                    alt={`${work.client} project image ${i + 2}`}
                    className="w-full rounded-2xl"
                  />
                </Reveal>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Quote */}
      {hasQuote && (
        <Section tone="mist">
          <Reveal>
            <figure className="max-w-3xl lg:mx-auto lg:text-center">
              <Icon name="quote" className="h-10 w-10 text-accent lg:mx-auto" />
              <blockquote className="mt-6 text-[clamp(1.5rem,2.8vw,2.1rem)] font-bold leading-[1.35] tracking-[-0.02em]">
                {work.quote.q}
              </blockquote>
              <figcaption className="mt-8">
                <div className="text-[15px] font-bold">{work.quote.n}</div>
                <div className="mt-0.5 text-[14px] text-neutral-500">
                  {work.quote.r}
                </div>
              </figcaption>
            </figure>
          </Reveal>
        </Section>
      )}

      {/* Next project */}
      {hasNext && (
        <Section tone={hasQuote ? "paper" : "mist"}>
          <Reveal>
            <Link
              to={`/work/${next.slug}`}
              className="group grid grid-cols-1 items-center gap-8 rounded-3xl border border-neutral-200 bg-white p-4 transition-shadow duration-500 hover:shadow-[0_30px_60px_-38px_rgba(11,16,23,0.35)] sm:p-5 lg:grid-cols-12 lg:gap-12"
            >
              <Img
                src={next.cover}
                monogram={initials(next.client)}
                alt={`${next.client} project`}
                className="aspect-[16/10] rounded-2xl lg:col-span-5 [&_img]:group-hover:scale-[1.04]"
              />
              <div className="px-3 pb-4 lg:col-span-7 lg:px-0 lg:pb-0 lg:pr-8">
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
                  Next project
                </span>
                <h2 className="mt-4 text-[clamp(1.8rem,3.2vw,2.6rem)] font-black leading-[1.05] tracking-[-0.03em]">
                  {next.client}
                </h2>
                {next.summary && (
                  <p className="mt-3 line-clamp-2 max-w-xl text-[16px] leading-[1.7] text-neutral-600">
                    {next.summary}
                  </p>
                )}
                <span className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-ink">
                  View project
                  <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        </Section>
      )}

      <FinalCta />
    </>
  )
}
